import type { JsonSchemaNode } from "@nmblr-platform-flow/schema";
import { useCallback, useEffect, useRef } from "react";
import { humanize } from "../../../lib/humanize.js";
import type { TemplateVariable } from "../../../lib/schema-paths.js";

interface Props {
	name: string;
	schema: JsonSchemaNode;
	value: unknown;
	onChange: (value: unknown) => void;
	required?: boolean;
	templateVariables?: TemplateVariable[];
}

type CodeLanguage = NonNullable<JsonSchemaNode["x-forge-code-language"]>;

const langLoaders: Record<CodeLanguage, () => Promise<{ default: () => unknown }>> = {
	sql: () => import("@codemirror/lang-sql").then((m) => ({ default: () => m.sql() })),
	json: () => import("@codemirror/lang-json").then((m) => ({ default: () => m.json() })),
	python: () => import("@codemirror/lang-python").then((m) => ({ default: () => m.python() })),
	javascript: () =>
		import("@codemirror/lang-javascript").then((m) => ({ default: () => m.javascript() })),
	yaml: () => import("@codemirror/lang-yaml").then((m) => ({ default: () => m.yaml() })),
};

function CodeEditorInner({ name, schema, value, onChange, required, templateVariables }: Props) {
	const containerRef = useRef<HTMLDivElement>(null);
	const viewRef = useRef<import("@codemirror/view").EditorView | null>(null);
	const onChangeRef = useRef(onChange);
	onChangeRef.current = onChange;

	const language = schema["x-forge-code-language"] ?? "json";
	const strValue = typeof value === "string" ? value : ((schema.default as string) ?? "");

	// biome-ignore lint/correctness/useExhaustiveDependencies: strValue intentionally excluded — editor owns its doc state after init
	const initEditor = useCallback(
		async (isMounted: () => boolean) => {
			if (!containerRef.current) return;

			const [{ EditorView, lineNumbers }, { EditorState }] = await Promise.all([
				import("@codemirror/view"),
				import("@codemirror/state"),
			]);

			// Component may have unmounted while awaiting imports
			if (!isMounted() || !containerRef.current) return;

			const forgeTheme = EditorView.theme(
				{
					"&": {
						background: "var(--forge-color-surface)",
						color: "var(--forge-color-text-primary)",
					},
					".cm-content": { caretColor: "var(--forge-color-accent)" },
					".cm-cursor": { borderLeftColor: "var(--forge-color-accent)" },
					".cm-gutters": {
						background: "var(--forge-color-elevated)",
						color: "var(--forge-color-text-muted)",
						border: "none",
						borderRight: "1px solid var(--forge-color-border)",
					},
					".cm-activeLineGutter": { background: "var(--forge-color-elevated)" },
					".cm-activeLine": { background: "transparent" },
					".cm-selectionBackground, ::selection": { background: "var(--forge-color-border)" },
				},
				{ dark: document.documentElement.dataset.theme === "dark" },
			);

			const extensions: import("@codemirror/state").Extension[] = [
				lineNumbers(),
				forgeTheme,
				EditorView.updateListener.of((update) => {
					if (update.docChanged) {
						onChangeRef.current(update.state.doc.toString());
					}
				}),
				EditorView.theme({
					"&": { fontSize: "12px", maxHeight: "300px" },
					".cm-scroller": { overflow: "auto" },
				}),
			];

			const loader = langLoaders[language];
			if (loader) {
				const langMod = await loader();
				extensions.push(langMod.default() as import("@codemirror/state").Extension);
			}

			if (templateVariables && templateVariables.length > 0 && schema["x-forge-template"]) {
				const { autocompletion } = await import("@codemirror/autocomplete");
				extensions.push(
					autocompletion({
						override: [
							(context) => {
								const before = context.state.doc.sliceString(0, context.pos);
								const triggerIndex = before.lastIndexOf("{{");
								if (triggerIndex === -1) return null;
								const afterTrigger = before.slice(triggerIndex);
								if (afterTrigger.includes("}}")) return null;
								const filter = before.slice(triggerIndex + 2).trim();
								return {
									from: triggerIndex,
									options: templateVariables
										.filter((v) => v.path.toLowerCase().includes(filter.toLowerCase()))
										.map((v) => ({ label: v.path, detail: v.type, apply: `{{ ${v.path} }}` })),
								};
							},
						],
					}),
				);
			}

			const state = EditorState.create({ doc: strValue, extensions });
			const view = new EditorView({ state, parent: containerRef.current });
			viewRef.current = view;

			return () => view.destroy();
		},
		[language, templateVariables, schema["x-forge-template"]],
	);

	useEffect(() => {
		let mounted = true;
		let cleanup: (() => void) | undefined;
		initEditor(() => mounted).then((c) => {
			if (mounted) cleanup = c;
		});
		return () => {
			mounted = false;
			cleanup?.();
		};
	}, [initEditor]);

	useEffect(() => {
		const view = viewRef.current;
		if (view && view.state.doc.toString() !== strValue) {
			view.dispatch({
				changes: { from: 0, to: view.state.doc.length, insert: strValue },
			});
		}
	}, [strValue]);

	const id = `field-${name}`;

	return (
		<div className="mb-3">
			<div className="flex items-center justify-between">
				<span className="text-xs text-text-muted">
					{humanize(name)}
					{required && <span className="ml-1 text-error">*</span>}
				</span>
				<span className="rounded-md bg-accent-bg px-1.5 py-0.5 font-mono text-xs text-accent">
					{language.toUpperCase()}
				</span>
			</div>
			<div
				ref={containerRef}
				id={id}
				className="mt-1 overflow-hidden rounded-xl border border-border"
			/>
			{schema.description && (
				<span className="mt-1 block text-xs text-text-muted">{schema.description}</span>
			)}
		</div>
	);
}

export { CodeEditorInner as CodeEditorLazy };
